/**
 * Utility functions for downloading files
 */
import logger from './logger';

/**
 * Required fields that must be present in the backend response for security validation
 */
const REQUIRED_BACKEND_FIELDS = [
  'ServerID',
  'ServerIDSign', 
  'SecureSiteSettingData',
  'SecureSiteSettingDataSign',
  'Settings'
];

/**
 * Required fields within the Settings object
 */
const REQUIRED_SETTINGS_FIELDS = [
  'UseFedRamp'
];

/**
 * Validates that all required backend fields are present in the response data
 * @param data - The response data to validate
 * @returns Object with validation result and missing fields
 */
export const validateBackendResponse = (data: any): { isValid: boolean; missingFields: string[] } => {
  const missingFields: string[] = [];
  
  // Check if data exists
  if (!data || typeof data !== 'object') {
    return { 
      isValid: false, 
      missingFields: [`Expected object response, received: ${typeof data}`] 
    };
  }
  
  // Check each required field
  REQUIRED_BACKEND_FIELDS.forEach(field => {
    if (!(field in data) || data[field] == null) {
      missingFields.push(field);
    }
  });
  
  // Validate Settings object fields
  if (data.Settings && typeof data.Settings === 'object') {
    REQUIRED_SETTINGS_FIELDS.forEach(field => {
      if (!(field in data.Settings) || data.Settings[field] == null) {
        missingFields.push(`Settings.${field}`);
      }
    });
  }
  
  return {
    isValid: missingFields.length === 0,
    missingFields
  };
};

/**
 * Sanitizes filename to prevent security issues
 * @param filename - The filename to sanitize
 * @returns Sanitized filename
 */
const sanitizeFilename = (filename: string): string => {
  return filename
    .replace(/[^a-zA-Z0-9._-]/g, '_') // Replace invalid characters
    .replace(/^\.+|\.+$/g, '') // Remove leading/trailing dots
    .replace(/_{2,}/g, '_') // Replace multiple underscores with single
    .substring(0, 100) // Limit length
    || 'config_file'; // Fallback
};


/**
 * Downloads a JSON object as a downloadable JSON file with production-grade features
 * @param data - The JSON object to download
 * @param filename - The name of the file (without extension)
 * @param skipValidation - Optional flag to skip security validation (default: false)
 * @returns Promise that resolves with download link info or rejects on error
 */
export const downloadJSON = async (
  data: any, 
  filename: string = 'config', 
  skipValidation: boolean = false
): Promise<{ downloadUrl: string; cleanup: () => void }> => {
  try {
    // Security validation: Check that all required backend fields are present
    if (!skipValidation) {
      const validation = validateBackendResponse(data);
      if (!validation.isValid) {
        throw new Error(`Security validation failed. Missing required fields: ${validation.missingFields.join(', ')}`);
      }
    }

    // Sanitize filename for security
    const sanitizedFilename = sanitizeFilename(filename);
    
    // Convert the data to a JSON string with proper formatting
    const jsonString = JSON.stringify(data, null, 2);
    
    // Check file size (warn if > 10MB)
    const fileSizeBytes = new Blob([jsonString]).size;
    const fileSizeMB = fileSizeBytes / (1024 * 1024);
    
    if (fileSizeMB > 10) {
      logger.warn(`Large file download: ${fileSizeMB.toFixed(2)}MB`);
    }
    
    // Create a Blob with the JSON content
    const blob = new Blob([jsonString], { 
      type: 'application/json;charset=utf-8' 
    });
    
    // Create a URL for the blob
    const url = URL.createObjectURL(blob);
    
    // Create a temporary anchor element
    const link = document.createElement('a');
    link.href = url;
    link.download = `${sanitizedFilename}.json`;
    link.style.display = 'none';
    
    // Add to DOM, trigger download, and clean up
    document.body.appendChild(link);
    
    try {
      // Trigger download immediately
      link.click();
      
      // Clean up DOM element after download starts
      // Use setTimeout to ensure download context is preserved
      setTimeout(() => {
        document.body.removeChild(link);
      }, 0);
      
    } catch (clickError) {
      // Clean up on error
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      throw new Error(`Download failed: ${clickError instanceof Error ? clickError.message : 'Unknown error'}`);
    }
    
    // Return the download info
    return {
      downloadUrl: url,
      cleanup: () => URL.revokeObjectURL(url)
    };
    
  } catch (error) {
    throw new Error(`Download preparation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};
